import React, { useState, useEffect } from 'react';
import {
  X, ExternalLink, Edit3, Save, Power, PowerOff, Video,
  ChevronDown, ChevronRight, Loader2, Download, Mic, Brain, CheckCircle2, XCircle,
  Settings, FileText, Info, Sparkles,
} from 'lucide-react';
import type { Strategy, StrategyParameters, ScreenerCriteria, SignalSubtypeResponse } from '../../types/strategy';
import {
  useUpdateStrategy, useActivateStrategy, useToggleUserActivation, useExtractStrategy,
  useSuggestParameters,
} from '../../hooks/useExpertStrategies';
import { useSignalSubtypes } from '../../hooks/useSignalSubtypes';

interface StrategyDetailModalProps {
  strategy: Strategy;
  currentUserId?: number;
  isAdmin?: boolean;
  onClose: () => void;
  onExtractingChange?: (extracting: boolean) => void;
}

const EXTRACTION_STEPS = [
  { key: 'pending', label: 'Starting extraction', icon: Video },
  { key: 'downloading', label: 'Downloading video', icon: Download },
  { key: 'transcribing', label: 'Transcribing & analyzing', icon: Mic },
  { key: 'structuring', label: 'Structuring strategy', icon: Brain },
  { key: 'completed', label: 'Extraction complete', icon: CheckCircle2 },
] as const;

type StepStatus = 'pending' | 'active' | 'completed' | 'failed';

function getStepStatus(stepKey: string, currentStatus: string | null): StepStatus {
  if (currentStatus === 'failed') return 'failed';

  const stepOrder = EXTRACTION_STEPS.map(s => s.key as string);
  const currentIdx = stepOrder.indexOf(currentStatus || '');
  const stepIdx = stepOrder.indexOf(stepKey);

  if (stepIdx < currentIdx) return 'completed';
  if (stepIdx === currentIdx) return currentStatus === 'completed' ? 'completed' : 'active';
  return 'pending';
}

const STEP_STYLES: Record<StepStatus, { line: string; dot: string; text: string; icon: string }> = {
  completed: {
    line: 'bg-emerald-500',
    dot: 'border-emerald-500 bg-emerald-500/20',
    text: 'text-emerald-400',
    icon: 'text-emerald-400',
  },
  active: {
    line: 'bg-slate-700',
    dot: 'border-purple-500 bg-purple-500/20 ring-2 ring-purple-500/30',
    text: 'text-white',
    icon: 'text-purple-400',
  },
  pending: {
    line: 'bg-slate-700',
    dot: 'border-slate-600 bg-slate-800',
    text: 'text-slate-500',
    icon: 'text-slate-500',
  },
  failed: {
    line: 'bg-slate-700',
    dot: 'border-red-500 bg-red-500/20',
    text: 'text-red-400',
    icon: 'text-red-400',
  },
};

const SESSIONS_OPTIONS = ['asian', 'london', 'new_york', 'overlap', 'sydney'] as const;
const CATEGORY_ORDER = ['Market Structure', 'Zone & Level', 'Price Delivery', 'Trend'];

function groupSubtypes(subtypes: SignalSubtypeResponse[]): Record<string, SignalSubtypeResponse[]> {
  return subtypes
    .filter(s => s.is_selectable)
    .reduce((acc, s) => {
      if (!acc[s.category]) acc[s.category] = [];
      acc[s.category].push(s);
      return acc;
    }, {} as Record<string, SignalSubtypeResponse[]>);
}

const StrategyDetailModal: React.FC<StrategyDetailModalProps> = ({
  strategy, currentUserId, isAdmin = false, onClose, onExtractingChange,
}) => {
  const isOwner = currentUserId !== undefined && strategy.user_id === currentUserId;
  // hotfix-1-168: admins can edit any strategy (including community strategies
  // they don't own). Backend grants the same override on PATCH + suggest.
  // Badge requires a logged-in user (currentUserId defined) so a bug elsewhere
  // can't render "Admin override" while the auth state is stale.
  const canEdit = isOwner || isAdmin;
  const isAdminOverride = !isOwner && isAdmin && currentUserId !== undefined;
  const [activeTab, setActiveTab] = useState<'strategy' | 'ea-config'>('strategy');
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(strategy.name);
  const [editContent, setEditContent] = useState(strategy.content);
  const [showTranscript, setShowTranscript] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  // EA Configuration form state
  const [selectedSubtypes, setSelectedSubtypes] = useState<string[]>([]);
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
  const [direction, setDirection] = useState<'both' | 'buy' | 'sell'>('both');
  const [minConfidence, setMinConfidence] = useState(65);
  const [slMultiplier, setSlMultiplier] = useState<string>('');
  const [tpMultiplier, setTpMultiplier] = useState<string>('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  // Screener Criteria state
  const [showScreenerCriteria, setShowScreenerCriteria] = useState(false);
  const [rsiBuyMax, setRsiBuyMax] = useState(30);
  const [rsiSellMin, setRsiSellMin] = useState(70);
  const [requirePriceAboveSma50, setRequirePriceAboveSma50] = useState(false);
  const [requireMacdCrossover, setRequireMacdCrossover] = useState(false);
  const [minIndicatorsAgree, setMinIndicatorsAgree] = useState(2);

  const updateMutation = useUpdateStrategy();
  const activateMutation = useActivateStrategy();
  const userActivationMutation = useToggleUserActivation();
  const extractMutation = useExtractStrategy();
  const suggestMutation = useSuggestParameters();
  const { data: subtypes = [] } = useSignalSubtypes();

  const isCommunity = strategy.type === 'community';
  const effectiveUserActive = strategy.user_is_active ?? true;

  const isExtracting = strategy.extraction_status !== null
    && !['completed', 'failed'].includes(strategy.extraction_status);

  useEffect(() => {
    onExtractingChange?.(isExtracting || extractMutation.isPending);
  }, [isExtracting, extractMutation.isPending, onExtractingChange]);

  useEffect(() => {
    if (!isExtracting && !extractMutation.isPending) {
      setElapsed(0);
      return;
    }
    const start = Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [isExtracting, extractMutation.isPending]);

  useEffect(() => {
    setEditName(strategy.name);
    setEditContent(strategy.content);
  }, [strategy]);

  // Pre-fill EA config from strategy.parameters
  useEffect(() => {
    if (strategy.parameters) {
      const sf = strategy.parameters.signal_filter ?? {};
      setSelectedSubtypes(sf.allowed_subtypes ?? []);
      setSelectedSessions(sf.active_sessions ?? []);
      setDirection(
        !sf.allowed_directions?.length || sf.allowed_directions.length > 1
          ? 'both'
          : (sf.allowed_directions[0].toLowerCase() as 'buy' | 'sell'),
      );
      setMinConfidence(sf.min_confidence ?? 65);
      const ea = strategy.parameters.ea_execution ?? {};
      const sc = strategy.parameters.screener_criteria;
      if (sc) {
        setRsiBuyMax(sc.rsi_buy_max ?? 30);
        setRsiSellMin(sc.rsi_sell_min ?? 70);
        setRequirePriceAboveSma50(sc.require_price_above_sma50 ?? false);
        setRequireMacdCrossover(sc.require_macd_crossover ?? false);
        setMinIndicatorsAgree(sc.min_indicators_agree ?? 2);
      }
      setSlMultiplier(ea.sl_atr_multiplier != null ? String(ea.sl_atr_multiplier) : '');
      setTpMultiplier(ea.tp_atr_multiplier != null ? String(ea.tp_atr_multiplier) : '');
    }
  }, [strategy.id]);

  const handleSave = () => {
    updateMutation.mutate(
      { id: strategy.id, data: { name: editName, content: editContent } },
      { onSuccess: () => setIsEditing(false) },
    );
  };

  const handleToggleActive = () => {
    activateMutation.mutate({ id: strategy.id, isActive: !strategy.is_active });
  };

  const handleExtract = () => {
    extractMutation.mutate(strategy.id);
  };

  const handleSaveEaConfig = () => {
    setSaveError(null);
    setSaveSuccess(false);

    const screener_criteria: ScreenerCriteria = {
      rsi_buy_max: rsiBuyMax,
      rsi_sell_min: rsiSellMin,
      require_price_above_sma50: requirePriceAboveSma50,
      require_macd_crossover: requireMacdCrossover,
      min_indicators_agree: minIndicatorsAgree,
    };

    const parameters: StrategyParameters = {
      version: 3,
      signal_filter: {
        allowed_subtypes: selectedSubtypes,
        allowed_directions: direction === 'both' ? [] : [direction.toUpperCase()],
        active_sessions: selectedSessions,
        min_confidence: minConfidence,
        max_spread_pips: null,
      },
      ea_execution: {
        sl_atr_multiplier: slMultiplier !== '' ? parseFloat(slMultiplier) : null,
        tp_atr_multiplier: tpMultiplier !== '' ? parseFloat(tpMultiplier) : null,
        zone_atr_sensitivity: null,
      },
      screener_criteria,
    };

    updateMutation.mutate(
      { id: strategy.id, data: { parameters } },
      {
        onSuccess: () => {
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 3000);
        },
        onError: (err) => {
          setSaveError(err instanceof Error ? err.message : 'Save failed');
        },
      },
    );
  };

  const toggleSubtype = (name: string) => {
    setSelectedSubtypes(prev =>
      prev.includes(name) ? prev.filter(s => s !== name) : [...prev, name],
    );
  };

  const toggleSession = (session: string) => {
    setSelectedSessions(prev =>
      prev.includes(session) ? prev.filter(s => s !== session) : [...prev, session],
    );
  };

  const handleSuggest = async () => {
    setSuggestError(null);
    try {
      const result = await suggestMutation.mutateAsync(strategy.id);
      const { parameters } = result;
      const sf = parameters.signal_filter ?? {};
      setSelectedSubtypes(sf.allowed_subtypes ?? []);
      setSelectedSessions(sf.active_sessions ?? []);
      setDirection(
        !sf.allowed_directions?.length || sf.allowed_directions.length > 1
          ? 'both'
          : (sf.allowed_directions[0].toLowerCase() as 'buy' | 'sell'),
      );
      setMinConfidence(sf.min_confidence ?? 65);
      const ea = parameters.ea_execution ?? {};
      setSlMultiplier(ea.sl_atr_multiplier != null ? String(ea.sl_atr_multiplier) : '');
      setTpMultiplier(ea.tp_atr_multiplier != null ? String(ea.tp_atr_multiplier) : '');
      const sc = parameters.screener_criteria;
      if (sc) {
        setRsiBuyMax(sc.rsi_buy_max ?? 30);
        setRsiSellMin(sc.rsi_sell_min ?? 70);
        setRequirePriceAboveSma50(sc.require_price_above_sma50 ?? false);
        setRequireMacdCrossover(sc.require_macd_crossover ?? false);
        setMinIndicatorsAgree(sc.min_indicators_agree ?? 2);
      }
    } catch (err) {
      setSuggestError(err instanceof Error ? err.message : 'Suggestion failed');
    }
  };

  const canExtract = isOwner
    && strategy.source_url
    && (!strategy.extraction_status || strategy.extraction_status === 'failed');

  const showProgress = isExtracting || extractMutation.isPending
    || strategy.extraction_status === 'completed'
    || strategy.extraction_status === 'failed';

  const statusColor: Record<string, string> = {
    pending: 'text-amber-400 bg-amber-500/10',
    approved: 'text-emerald-400 bg-emerald-500/10',
    approved_with_notes: 'text-teal-400 bg-teal-500/10',
    rejected: 'text-red-400 bg-red-500/10',
  };

  const grouped = groupSubtypes(subtypes);
  const orderedCategories = [
    ...CATEGORY_ORDER.filter(c => grouped[c]),
    ...Object.keys(grouped).filter(c => !CATEGORY_ORDER.includes(c)),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-slate-900 border border-slate-700 rounded-xl shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <div className="flex items-center gap-3 min-w-0">
            {isEditing && activeTab === 'strategy' ? (
              <input
                className="text-lg font-semibold text-white bg-slate-800 border border-slate-600 rounded px-2 py-1 w-full"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            ) : (
              <h2 className="text-lg font-semibold text-white truncate">{strategy.name}</h2>
            )}
            <span className={`text-[10px] px-2 py-0.5 rounded ${statusColor[strategy.status] || 'text-slate-400 bg-slate-700'}`}>
              {strategy.status}
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded bg-slate-700 text-slate-300">
              {strategy.type === 'community' ? 'Community' : 'Private'}
            </span>
            {isAdminOverride && (
              <span
                className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40"
                title="You are editing this strategy as an admin (you are not the owner). Changes affect all users of this community strategy."
              >
                Admin override
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div role="tablist" className="flex border-b border-slate-700 px-6">
          <button
            role="tab"
            aria-selected={activeTab === 'strategy'}
            onClick={() => setActiveTab('strategy')}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition ${
              activeTab === 'strategy'
                ? 'border-purple-500 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <FileText size={13} />
            Strategy
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'ea-config'}
            onClick={() => setActiveTab('ea-config')}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition ${
              activeTab === 'ea-config'
                ? 'border-purple-500 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Settings size={13} />
            Signal Config
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* ──── STRATEGY TAB ──── */}
          {activeTab === 'strategy' && (
            <>
              {/* Meta row */}
              <div className="flex flex-wrap gap-4 text-xs text-slate-400">
                {strategy.win_rate !== null && (
                  <span>Win Rate: <span className="text-slate-200">{strategy.win_rate?.toFixed(1)}%</span></span>
                )}
                {strategy.total_trades > 0 && (
                  <span>Trades: <span className="text-slate-200">{strategy.total_trades}</span></span>
                )}
                {strategy.validation_score !== null && (
                  <span>Score: <span className="text-slate-200">{strategy.validation_score}/100</span></span>
                )}
                {strategy.source_url && (
                  <a
                    href={strategy.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 flex items-center gap-1"
                  >
                    Source <ExternalLink size={10} />
                  </a>
                )}
              </div>

              {/* Extraction Progress Tracker */}
              {showProgress && (
                <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wide">
                      Video Extraction
                    </h3>
                    {(isExtracting || extractMutation.isPending) && (
                      <span className="text-[10px] text-purple-400 flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-purple-500" />
                        </span>
                        Running... {elapsed}s
                      </span>
                    )}
                  </div>

                  <div className="space-y-0">
                    {EXTRACTION_STEPS.map((step, i) => {
                      const status = extractMutation.isPending && !strategy.extraction_status
                        ? (i === 0 ? 'active' : 'pending')
                        : getStepStatus(step.key, strategy.extraction_status);
                      const styles = STEP_STYLES[status];
                      const Icon = status === 'failed' ? XCircle
                        : status === 'completed' ? CheckCircle2
                          : step.icon;
                      const isLast = i === EXTRACTION_STEPS.length - 1;

                      return (
                        <div key={step.key} className="flex items-start gap-3">
                          <div className="flex flex-col items-center">
                            <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 ${styles.dot}`}>
                              {status === 'active' ? (
                                <Loader2 size={14} className={`animate-spin ${styles.icon}`} />
                              ) : (
                                <Icon size={14} className={styles.icon} />
                              )}
                            </div>
                            {!isLast && (
                              <div className={`w-0.5 h-5 ${styles.line}`} />
                            )}
                          </div>
                          <div className="pt-1">
                            <span className={`text-xs font-medium ${styles.text}`}>
                              {step.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {strategy.extraction_status === 'failed' && strategy.extraction_metadata?.error != null ? (
                    <p className="mt-3 text-[11px] text-red-400/80 bg-red-500/5 rounded px-3 py-2">
                      {String(strategy.extraction_metadata.error)}
                    </p>
                  ) : null}

                  {extractMutation.isError ? (
                    <p className="mt-3 text-[11px] text-red-400/80 bg-red-500/5 rounded px-3 py-2">
                      {extractMutation.error instanceof Error ? extractMutation.error.message : 'Extraction failed'}
                    </p>
                  ) : null}

                  {strategy.extraction_status === 'completed' && strategy.extraction_metadata?.duration_seconds != null ? (
                    <p className="mt-2 text-[10px] text-slate-500">
                      Completed in {Math.round(Number(strategy.extraction_metadata.duration_seconds))}s
                      {strategy.extraction_metadata?.pipeline ? ` via ${String(strategy.extraction_metadata.pipeline)}` : ''}
                    </p>
                  ) : null}
                </div>
              )}

              {/* Content */}
              {isEditing ? (
                <textarea
                  className="w-full h-80 bg-slate-800 border border-slate-600 rounded-lg p-3 text-sm text-slate-200 font-mono resize-y"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                />
              ) : (
                <div className="bg-slate-800/60 rounded-lg p-4">
                  <pre className="text-sm text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">
                    {strategy.content}
                  </pre>
                </div>
              )}

              {/* Transcript section */}
              {strategy.raw_transcript && (
                <div className="border border-slate-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setShowTranscript(!showTranscript)}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-medium text-slate-300 hover:text-white bg-slate-800/40 hover:bg-slate-800/60 transition"
                  >
                    {showTranscript ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    Transcript
                  </button>
                  {showTranscript && (
                    <div className="px-4 py-3 bg-slate-800/20 border-t border-slate-700">
                      <pre className="text-xs text-slate-400 whitespace-pre-wrap font-sans leading-relaxed max-h-60 overflow-y-auto">
                        {strategy.raw_transcript}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Timestamps */}
              <div className="text-[10px] text-slate-500 flex gap-4">
                {strategy.created_at && (
                  <span>Created: {new Date(strategy.created_at).toLocaleDateString()}</span>
                )}
                {strategy.updated_at && (
                  <span>Updated: {new Date(strategy.updated_at).toLocaleDateString()}</span>
                )}
              </div>
            </>
          )}

          {/* ──── EA CONFIGURATION TAB ──── */}
          {activeTab === 'ea-config' && (
            <div className="space-y-5">
              {/* Signal Subtype Picker */}
              <div>
                <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-2">
                  Signal Subtypes
                </h3>
                <p className="text-[11px] text-slate-500 mb-3">
                  Select which ICT/SMC patterns trigger this strategy. Empty = all patterns allowed.
                </p>
                {orderedCategories.length === 0 ? (
                  <p className="text-xs text-slate-500">Loading subtypes…</p>
                ) : (
                  <div className="space-y-3">
                    {orderedCategories.map(category => (
                      <div key={category}>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                          {category}
                        </p>
                        <div className="grid grid-cols-2 gap-1">
                          {grouped[category].map(subtype => (
                            <label
                              key={subtype.name}
                              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-800/60 hover:bg-slate-800 cursor-pointer group"
                              title={subtype.description}
                            >
                              <input
                                type="checkbox"
                                className="accent-purple-500 shrink-0"
                                checked={selectedSubtypes.includes(subtype.name)}
                                onChange={() => toggleSubtype(subtype.name)}
                              />
                              <span className="text-xs text-slate-300 group-hover:text-white truncate">
                                {subtype.display_name}
                              </span>
                              <Info size={11} className="text-slate-600 group-hover:text-slate-400 shrink-0 ml-auto" />
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sessions */}
              <div>
                <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-2">
                  Active Sessions
                </h3>
                <div className="flex flex-wrap gap-2">
                  {SESSIONS_OPTIONS.map(session => (
                    <button
                      key={session}
                      type="button"
                      onClick={() => toggleSession(session)}
                      className={`text-xs px-3 py-1 rounded-full border transition capitalize ${
                        selectedSessions.includes(session)
                          ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                          : 'border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300'
                      }`}
                    >
                      {session.replace('_', ' ')}
                    </button>
                  ))}
                  {selectedSessions.length === 0 && (
                    <span className="text-[11px] text-slate-500 self-center">All sessions</span>
                  )}
                </div>
              </div>

              {/* Direction */}
              <div>
                <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-2">
                  Direction
                </h3>
                <div className="flex gap-3">
                  {(['both', 'buy', 'sell'] as const).map(d => (
                    <label key={d} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="radio"
                        name="direction"
                        value={d}
                        checked={direction === d}
                        onChange={() => setDirection(d)}
                        className="accent-purple-500"
                      />
                      <span className="text-xs text-slate-300 capitalize">{d === 'both' ? 'Both' : d.toUpperCase()}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Min Confidence */}
              <div>
                <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-2">
                  Min Confidence: <span className="text-purple-400 font-mono">{minConfidence}</span>
                </h3>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={minConfidence}
                  onChange={(e) => setMinConfidence(parseInt(e.target.value, 10))}
                  className="w-full accent-purple-500"
                />
                <div className="flex justify-between text-[10px] text-slate-500 mt-0.5">
                  <span>0</span>
                  <span>50</span>
                  <span>100</span>
                </div>
              </div>

              {/* SL/TP Multipliers */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-1.5">
                    SL Multiplier
                  </h3>
                  <input
                    type="number"
                    min={0.1}
                    max={10}
                    step={0.1}
                    placeholder="EA default"
                    value={slMultiplier}
                    onChange={(e) => setSlMultiplier(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-slate-200 placeholder-slate-500"
                  />
                </div>
                <div>
                  <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-1.5">
                    TP Multiplier
                  </h3>
                  <input
                    type="number"
                    min={0.1}
                    max={10}
                    step={0.1}
                    placeholder="EA default"
                    value={tpMultiplier}
                    onChange={(e) => setTpMultiplier(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-slate-200 placeholder-slate-500"
                  />
                </div>
              </div>

              {/* Screener Criteria — collapsible, editable by owner or admin */}
              {canEdit && (
                <div className="border border-slate-700 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowScreenerCriteria(prev => !prev)}
                    className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-800/60 hover:bg-slate-800 transition text-left"
                  >
                    <span className="text-xs font-semibold text-slate-300 uppercase tracking-wide">
                      Screener Criteria
                    </span>
                    {showScreenerCriteria ? (
                      <ChevronDown size={14} className="text-slate-400" />
                    ) : (
                      <ChevronRight size={14} className="text-slate-400" />
                    )}
                  </button>
                  {showScreenerCriteria && (
                    <div className="px-3 py-3 space-y-4 bg-slate-900/40">
                      <p className="text-[11px] text-slate-500">
                        Tune indicator thresholds used by the screener when this strategy is active.
                      </p>

                      {/* RSI Buy Threshold */}
                      <div>
                        <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-1">
                          RSI Buy Max: <span className="text-teal-400 font-mono">{rsiBuyMax}</span>
                        </h4>
                        <input
                          type="range"
                          min={10}
                          max={50}
                          step={1}
                          value={rsiBuyMax}
                          onChange={(e) => setRsiBuyMax(parseInt(e.target.value, 10))}
                          className="w-full accent-teal-500"
                        />
                        <div className="flex justify-between text-[10px] text-slate-500 mt-0.5">
                          <span>10</span>
                          <span>30</span>
                          <span>50</span>
                        </div>
                      </div>

                      {/* RSI Sell Threshold */}
                      <div>
                        <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-1">
                          RSI Sell Min: <span className="text-teal-400 font-mono">{rsiSellMin}</span>
                        </h4>
                        <input
                          type="range"
                          min={50}
                          max={90}
                          step={1}
                          value={rsiSellMin}
                          onChange={(e) => setRsiSellMin(parseInt(e.target.value, 10))}
                          className="w-full accent-teal-500"
                        />
                        <div className="flex justify-between text-[10px] text-slate-500 mt-0.5">
                          <span>50</span>
                          <span>70</span>
                          <span>90</span>
                        </div>
                      </div>

                      {/* Min Indicators in Agreement */}
                      <div>
                        <h4 className="text-xs font-semibold text-slate-300 uppercase tracking-wide mb-1.5">
                          Min Indicators in Agreement
                        </h4>
                        <div className="flex gap-2">
                          {([1, 2, 3, 4] as const).map(n => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setMinIndicatorsAgree(n)}
                              className={`text-xs px-3 py-1 rounded-full border transition ${
                                minIndicatorsAgree === n
                                  ? 'border-teal-500 bg-teal-500/20 text-teal-300'
                                  : 'border-slate-600 text-slate-400 hover:border-slate-500'
                              }`}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Toggles */}
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="accent-teal-500"
                            checked={requirePriceAboveSma50}
                            onChange={(e) => setRequirePriceAboveSma50(e.target.checked)}
                          />
                          <span className="text-xs text-slate-300">Price must be above SMA50</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="accent-teal-500"
                            checked={requireMacdCrossover}
                            onChange={(e) => setRequireMacdCrossover(e.target.checked)}
                          />
                          <span className="text-xs text-slate-300">Require MACD crossover</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error / Success */}
              {suggestError && (
                <p className="text-[11px] text-amber-400 bg-amber-500/10 rounded px-3 py-2">
                  Suggest failed: {suggestError}
                </p>
              )}
              {saveError && (
                <p className="text-[11px] text-red-400 bg-red-500/10 rounded px-3 py-2">
                  {saveError}
                </p>
              )}
              {saveSuccess && (
                <p className="text-[11px] text-emerald-400 bg-emerald-500/10 rounded px-3 py-2">
                  Signal configuration saved.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-700">
          {/* Left side: ownership actions */}
          {activeTab === 'strategy' && (
            isOwner ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleToggleActive}
                  disabled={activateMutation.isPending}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition ${
                    strategy.is_active
                      ? 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                      : 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'
                  }`}
                >
                  {strategy.is_active ? <PowerOff size={12} /> : <Power size={12} />}
                  {strategy.is_active ? 'Deactivate' : 'Activate'}
                </button>

                {canExtract && (
                  <button
                    onClick={handleExtract}
                    disabled={extractMutation.isPending || isExtracting}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-purple-500/30 text-purple-400 hover:bg-purple-500/10 transition disabled:opacity-50"
                  >
                    {extractMutation.isPending ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Video size={12} />
                    )}
                    {extractMutation.isPending ? 'Extracting...' : 'Extract from Video'}
                  </button>
                )}
              </div>
            ) : isCommunity ? (
              <button
                onClick={() => userActivationMutation.mutate({ id: strategy.id, isActive: !effectiveUserActive })}
                disabled={userActivationMutation.isPending}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition ${
                  effectiveUserActive
                    ? 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                    : 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'
                }`}
              >
                {effectiveUserActive ? <PowerOff size={12} /> : <Power size={12} />}
                {effectiveUserActive ? 'Deactivate for Me' : 'Activate for Me'}
              </button>
            ) : (
              <span className="text-[10px] text-slate-500">Read-only</span>
            )
          )}

          {activeTab === 'ea-config' && canEdit && strategy.content && (
            <button
              type="button"
              onClick={handleSuggest}
              disabled={suggestMutation.isPending || updateMutation.isPending}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-purple-500/30 text-purple-400 hover:bg-purple-500/10 transition disabled:opacity-50"
            >
              {suggestMutation.isPending ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Sparkles size={12} />
              )}
              {suggestMutation.isPending ? 'Suggesting...' : 'Suggest from Content'}
            </button>
          )}

          {/* Right side: edit/save */}
          <div className="flex gap-2">
            {activeTab === 'strategy' && canEdit && (
              isEditing ? (
                <>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-slate-600 text-slate-400 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-teal-500/20 text-teal-400 hover:bg-teal-500/30"
                  >
                    <Save size={12} />
                    Save
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-600 text-slate-400 hover:text-white"
                >
                  <Edit3 size={12} />
                  Edit
                </button>
              )
            )}

            {activeTab === 'ea-config' && canEdit && (
              <button
                onClick={handleSaveEaConfig}
                disabled={updateMutation.isPending}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition disabled:opacity-50"
              >
                {updateMutation.isPending ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Save size={12} />
                )}
                Save Signal Config
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategyDetailModal;
